import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Shield, Pencil, Trash2, Users as UsersIcon, ImagePlus, Search, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/clubs")({
  component: AdminClubs,
});

interface Club {
  id: string;
  name: string;
  logo_url: string | null;
  description: string | null;
  website: string | null;
  is_verified: boolean;
  created_at: string;
}

interface Member {
  id: string;
  club_id: string;
  user_id: string;
  role: "club_admin" | "club_member";
  status: "pending" | "approved" | "rejected";
  requested_at: string;
  profile: { nickname: string; avatar_url: string | null } | null;
}

const emptyForm = { name: "", description: "", website: "", logo_url: "", is_verified: false };

function AdminClubs() {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [openClub, setOpenClub] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Club | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [signedLogos, setSignedLogos] = useState<Record<string, string>>({});
  const [formLogoPreview, setFormLogoPreview] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const v = form.logo_url;
      if (!v) { setFormLogoPreview(""); return; }
      if (v.startsWith("http")) { setFormLogoPreview(v); return; }
      const { data } = await supabase.storage.from("club-logos").createSignedUrl(v, 60 * 60);
      if (!cancelled) setFormLogoPreview(data?.signedUrl ?? "");
    })();
    return () => { cancelled = true; };
  }, [form.logo_url]);

  const load = async () => {
    const { data } = await supabase
      .from("clubs")
      .select("*")
      .order("created_at", { ascending: false });
    const list = (data as Club[]) ?? [];
    setClubs(list);

    // Resolve signed URLs for logo paths
    const next: Record<string, string> = {};
    await Promise.all(
      list.map(async (c) => {
        if (!c.logo_url) return;
        if (c.logo_url.startsWith("http")) { next[c.id] = c.logo_url; return; }
        const { data: signed } = await supabase.storage
          .from("club-logos")
          .createSignedUrl(c.logo_url, 60 * 60);
        if (signed?.signedUrl) next[c.id] = signed.signedUrl;
      })
    );
    setSignedLogos(next);
  };

  const loadMembers = async (clubId: string) => {
    const { data: rows } = await supabase
      .from("club_members")
      .select("id, club_id, user_id, role, status, requested_at")
      .eq("club_id", clubId)
      .order("requested_at", { ascending: false });
    const ids = (rows ?? []).map((r: any) => r.user_id);
    const { data: profs } = ids.length
      ? await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids)
      : { data: [] as any[] };
    const map = new Map((profs ?? []).map((p: any) => [p.id, p]));
    setMembers((prev) => ({
      ...prev,
      [clubId]: (rows ?? []).map((r: any) => ({ ...r, profile: map.get(r.user_id) ?? null })),
    }));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (c: Club) => {
    setEditing(c);
    setForm({
      name: c.name,
      description: c.description ?? "",
      website: c.website ?? "",
      logo_url: c.logo_url ?? "",
      is_verified: c.is_verified,
    });
    setFormOpen(true);
  };

  const uploadLogo = async (file: File) => {
    setUploading(true);
    const ext = file.name.split(".").pop() ?? "png";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("club-logos").upload(path, file, { upsert: false });
    setUploading(false);
    if (error) { toast.error(error.message); return; }
    setForm((f) => ({ ...f, logo_url: path }));
    toast.success("Logo subido");
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      website: form.website.trim() || null,
      logo_url: form.logo_url.trim() || null,
      is_verified: form.is_verified,
    };
    const { error } = editing
      ? await supabase.from("clubs").update(payload).eq("id", editing.id)
      : await supabase.from("clubs").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Club actualizado" : "Club creado");
    setFormOpen(false);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("¿Eliminar este club? Esta acción no se puede deshacer.")) return;
    const { error } = await supabase.from("clubs").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Club eliminado");
    load();
  };

  const reviewMember = async (id: string, clubId: string, status: "approved" | "rejected") => {
    const { error } = await supabase
      .from("club_members")
      .update({ status, reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(status === "approved" ? "Miembro aprobado" : "Solicitud rechazada");
    loadMembers(clubId);
  };

  const toggleRole = async (id: string, clubId: string, role: Member["role"]) => {
    const next = role === "club_admin" ? "club_member" : "club_admin";
    const { error } = await supabase.from("club_members").update({ role: next }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Rol actualizado");
    loadMembers(clubId);
  };

  const removeMember = async (id: string, clubId: string) => {
    if (!confirm("¿Eliminar este miembro del club?")) return;
    const { error } = await supabase.from("club_members").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Miembro eliminado");
    loadMembers(clubId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl mb-1">Clubs</h1>
          <p className="text-sm text-muted-foreground">Gestiona clubs verificados y sus miembros.</p>
        </div>
        <button
          onClick={openCreate}
          className="shrink-0 rounded-full bg-[#F5C518] text-black px-4 py-2 text-sm font-semibold"
        >
          + Nuevo club
        </button>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl md:rounded-3xl border border-border bg-[#1a1a1a] p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-xl">{editing ? "Editar club" : "Nuevo club"}</h2>
              <button onClick={() => setFormOpen(false)} className="rounded-full bg-surface-elevated p-1.5"><X className="h-4 w-4" /></button>
            </div>

            <label className="block text-xs uppercase tracking-wider text-white/50">Nombre</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-border bg-[#0f0f0f] px-3 py-2.5 text-sm"
              placeholder="Nombre del club"
            />

            <label className="block text-xs uppercase tracking-wider text-white/50">Logo</label>
            <label className="group relative block cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }}
                className="sr-only"
              />
              {form.logo_url ? (
                <div className="flex items-center gap-3 rounded-2xl border border-dashed border-border bg-[#0f0f0f] p-3 transition group-hover:border-[#F5C518]/60 group-hover:bg-[#141414]">
                  <div className="relative h-16 w-16 rounded-full overflow-hidden bg-surface-elevated shrink-0">
                    <img
                      src={formLogoPreview}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                    <div className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/60 text-[10px] font-semibold text-white text-center px-1">
                      Cambiar logo
                    </div>
                  </div>
                  <div className="min-w-0 text-xs text-white/60 truncate">
                    {uploading ? "Subiendo…" : "Logo cargado. Haz clic para cambiarlo."}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-[#0f0f0f] py-6 px-3 text-center transition group-hover:border-[#F5C518]/60 group-hover:bg-[#141414]">
                  <div className="h-10 w-10 rounded-full bg-surface-elevated flex items-center justify-center">
                    <ImagePlus className="h-5 w-5 text-[#F5C518]" />
                  </div>
                  <div className="text-sm font-medium">{uploading ? "Subiendo…" : "Añadir logo del club"}</div>
                  <div className="text-[11px] text-white/50">PNG, JPG · clic para seleccionar</div>
                </div>
              )}
            </label>

            <label className="block text-xs uppercase tracking-wider text-white/50">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl border border-border bg-[#0f0f0f] px-3 py-2 text-sm min-h-[80px]"
              placeholder="Opcional"
            />

            <label className="block text-xs uppercase tracking-wider text-white/50">Web</label>
            <input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="w-full rounded-xl border border-border bg-[#0f0f0f] px-3 py-2.5 text-sm"
              placeholder="https://…"
            />

            <label className="flex items-center gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={form.is_verified}
                onChange={(e) => setForm({ ...form, is_verified: e.target.checked })}
              />
              Verificado
            </label>

            <button
              onClick={save}
              className="w-full rounded-full bg-[#F5C518] text-black px-4 py-2.5 text-sm font-semibold mt-2"
            >
              {editing ? "Guardar cambios" : "Crear club"}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-[#1a1a1a] divide-y divide-border">
        {clubs.length === 0 ? (
          <div className="p-6 text-center text-sm text-white/60">Aún no hay clubs.</div>
        ) : clubs.map((c) => {
          const expanded = openClub === c.id;
          const ms = members[c.id] ?? [];
          return (
            <div key={c.id} className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-surface-elevated flex items-center justify-center">
                    {signedLogos[c.id] ? (
                      <img src={signedLogos[c.id]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Shield className="h-4 w-4 text-[#F5C518]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {c.name}
                      {c.is_verified && <span className="text-[10px] rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5">Verificado</span>}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">{c.website ?? "—"}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => { const next = expanded ? null : c.id; setOpenClub(next); if (next) loadMembers(c.id); }}
                    className="flex items-center gap-1 rounded-full bg-[#0f0f0f] border border-border px-3 py-1.5 text-xs"
                  >
                    <UsersIcon className="h-3.5 w-3.5" /> Miembros
                  </button>
                  <button
                    onClick={() => openEdit(c)}
                    className="flex items-center gap-1 rounded-full bg-[#0f0f0f] border border-border px-3 py-1.5 text-xs"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    className="flex items-center gap-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 px-3 py-1.5 text-xs font-semibold"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar
                  </button>
                </div>
              </div>

              {expanded && (
                <div className="space-y-3">
                  <AddMemberSearch
                    clubId={c.id}
                    existingIds={ms.map((x) => x.user_id)}
                    onAdded={() => loadMembers(c.id)}
                  />
                  <div className="rounded-xl border border-border bg-[#0f0f0f] divide-y divide-border">
                    {ms.length === 0 ? (
                      <div className="p-4 text-center text-xs text-white/50">Sin miembros.</div>
                    ) : ms.map((m) => (
                      <div key={m.id} className="flex items-center justify-between gap-3 p-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full overflow-hidden bg-surface-elevated flex items-center justify-center">
                            {m.profile?.avatar_url ? (
                              <img src={m.profile.avatar_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="text-xs font-bold">{m.profile?.nickname?.[0]?.toUpperCase() ?? "?"}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">@{m.profile?.nickname ?? "—"}</div>
                            <div className="text-[11px] text-white/50 flex gap-2">
                              <span className={
                                m.status === "approved" ? "text-emerald-400" :
                                m.status === "rejected" ? "text-red-400" : "text-amber-400"
                              }>{m.status}</span>
                              <span>· {m.role}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          {m.status === "pending" && (
                            <>
                              <button
                                onClick={() => reviewMember(m.id, c.id, "approved")}
                                className="flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-semibold"
                              >
                                <Check className="h-3 w-3" /> Aprobar
                              </button>
                              <button
                                onClick={() => reviewMember(m.id, c.id, "rejected")}
                                className="flex items-center gap-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 px-2.5 py-1 text-[11px] font-semibold"
                              >
                                <X className="h-3 w-3" /> Rechazar
                              </button>
                            </>
                          )}
                          {m.status === "approved" && (
                            <button
                              onClick={() => toggleRole(m.id, c.id, m.role)}
                              className="rounded-full bg-[#1a1a1a] border border-border px-2.5 py-1 text-[11px]"
                            >
                              {m.role === "club_admin" ? "Hacer miembro" : "Hacer admin"}
                            </button>
                          )}
                          <button
                            onClick={() => removeMember(m.id, c.id)}
                            className="rounded-full bg-[#1a1a1a] border border-border p-1.5"
                            aria-label="Eliminar"
                          >
                            <Trash2 className="h-3 w-3 text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SearchProfile {
  id: string;
  nickname: string;
  avatar_url: string | null;
}

function AddMemberSearch({
  clubId,
  existingIds,
  onAdded,
}: {
  clubId: string;
  existingIds: string[];
  onAdded: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .ilike("nickname", `%${text}%`)
        .limit(10);
      if (!cancelled) {
        setResults((data as SearchProfile[]) ?? []);
        setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const addMember = async (userId: string) => {
    if (existingIds.includes(userId)) {
      toast.error("Ya es miembro");
      return;
    }
    setAddingId(userId);
    const { error } = await supabase.from("club_members").insert({
      club_id: clubId,
      user_id: userId,
      status: "approved",
      role: "club_member",
    });
    setAddingId(null);
    if (error) {
      if ((error as any).code === "23505") {
        toast.error("Ya es miembro");
      } else {
        toast.error(error.message);
      }
      return;
    }
    toast.success("Miembro añadido");
    setQ("");
    setResults([]);
    onAdded();
  };

  return (
    <div className="rounded-xl border border-border bg-[#0f0f0f] p-3 space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar rider por nickname para añadir..."
          className="w-full rounded-full border border-border bg-[#1a1a1a] pl-9 pr-3 py-2 text-sm placeholder:text-white/40"
        />
      </div>
      {q.trim().length >= 2 && (
        <div className="divide-y divide-border rounded-lg overflow-hidden border border-border">
          {searching ? (
            <div className="p-3 text-center text-xs text-white/50">Buscando…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-xs text-white/50">No se encontraron riders</div>
          ) : results.map((p) => {
            const isMember = existingIds.includes(p.id);
            return (
              <div key={p.id} className="flex items-center justify-between gap-3 p-2.5 bg-[#0f0f0f]">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-8 w-8 rounded-full overflow-hidden bg-surface-elevated flex items-center justify-center shrink-0">
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold">{p.nickname?.[0]?.toUpperCase() ?? "?"}</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold truncate">@{p.nickname}</div>
                </div>
                {isMember ? (
                  <span className="text-[11px] text-white/50 shrink-0">Ya es miembro</span>
                ) : (
                  <button
                    onClick={() => addMember(p.id)}
                    disabled={addingId === p.id}
                    className="flex items-center gap-1 rounded-full bg-[#F5C518] text-black px-3 py-1 text-[11px] font-semibold disabled:opacity-50 shrink-0"
                  >
                    <Plus className="h-3 w-3" /> {addingId === p.id ? "Añadiendo…" : "Añadir al club"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
